import { Fragment, useEffect, useState } from 'react';
// @ts-ignore - no type declarations for 'react-html-parser'
import ReactHtmlParser from 'react-html-parser';
import { useTranslation } from 'react-i18next';

import { Typography } from '@mui/material';
import { getIn } from 'formik';

import { Control, FormCard } from '../../../../components';
import { CARD_CATEGORIES } from '../../../../shared/cardConfig/constants';
import {
  getAddressConfigByCategory as getAddressConfigByCategoryRaw,
  getAddressFieldConfig as getAddressFieldConfigRaw
} from '../../../../shared/cardConfig/helpers';
import { taxExemptUploadDialog } from '../../../../shared/dialogConfigs';
import { SOLD_TO_ADDRESS_FIELDS } from '../../../../shared/fieldConfig/constants';
import { clearFieldError } from '../../../../store/fieldError';
import { CONTROL_TYPE } from '../../../../utils/constants';
import { handleRemoveFile, openUploadDialog } from '../../../../utils/fileUploadHelpers';
import { VALIDATION } from '../../../../utils/validation';
import AddTaxNumberButton from '../../../AddTaxNumberButton';
import AttachmentUpload from '../../../AttachmentUpload';
import type {
  AddressFieldConfig,
  CardDispatch,
  CardErrorMustContain,
  CardField,
  CardFieldComponent,
  CardFieldProps,
  CardStore,
  TFunc
} from '../../types';

const category = CARD_CATEGORIES.soldTo;
const companycategory = CARD_CATEGORIES.company;

// These helpers are authored in JS with JSDoc types that are stricter than
// these call sites' runtime usage; give them explicit (mostly optional)
// argument types and concrete result shapes the cards can rely on.
const getAddressFieldConfig = getAddressFieldConfigRaw as unknown as (
  field: string,
  t: TFunc,
  disabled?: boolean,
  maxCharacters?: number | null,
  category?: string,
  isOptional?: boolean,
  controlType?: string
) => AddressFieldConfig;
const getAddressConfigByCategory = getAddressConfigByCategoryRaw as (
  category: string,
  t: TFunc,
  store?: CardStore,
  isCardOptional?: boolean,
  multiShipToIndex?: number,
  disabled?: boolean
) => AddressFieldConfig[];

interface SoldToAddressCardProps {
  isReview?: boolean;
  store: CardStore;
  fields: CardField[];
  errorMustContain?: CardErrorMustContain;
  [key: string]: unknown;
}

function TaxNumber1({ t, disabled, store, required }: CardFieldProps) {
  const { customer } = store;
  const { [category]: { address: { naturalPerson } = {} } = {}, region = '', countryCode = '' } = customer;
  const isTaxNumber1Required = region.toLowerCase() === 'latam' && countryCode === 'MX' ? !naturalPerson : false;
  const field = 'taxNumber1';
  const name = `${category}.address.${field}`;
  const config = {
    name,
    category,
    field,
    label: t('address.taxNumber1.label'),
    validations: [{ type: VALIDATION.numberOfAlphaNumeric }, { type: VALIDATION.maxCharacters, param: 16 }],
    required: required || isTaxNumber1Required,
    disabled,
    placeholder: t('address.taxNumber1.placeholder')
  };

  return <Control {...config} />;
}

function TaxNumber2({ t, disabled }: CardFieldProps) {
  const field = 'taxNumber2';
  const name = `${category}.address.${field}`;
  const config = {
    name,
    category,
    field,
    label: t('address.taxNumber2.label'),
    validations: [{ type: VALIDATION.numberOfAlphaNumeric }, { type: VALIDATION.maxCharacters, param: 16 }],
    disabled,
    placeholder: t('address.taxNumber2.placeholder')
  };

  return <Control {...config} />;
}

function TaxNumber3({ t, disabled }: CardFieldProps) {
  const field = 'taxNumber3';
  const name = `${category}.address.${field}`;
  const config = {
    name,
    category,
    field,
    label: t('address.taxNumber3.label'),
    validations: [{ type: VALIDATION.numberOfAlphaNumeric }, { type: VALIDATION.maxCharacters, param: 16 }],
    disabled,
    placeholder: t('address.taxNumber3.placeholder')
  };

  return <Control {...config} />;
}

function TaxNumber4({ t, disabled }: CardFieldProps) {
  const field = 'taxNumber4';
  const name = `${category}.address.${field}`;
  const config = {
    name,
    category,
    field,
    label: t('address.taxNumber4.label'),
    validations: [{ type: VALIDATION.numberOfAlphaNumeric }, { type: VALIDATION.maxCharacters, param: 16 }],
    disabled,
    placeholder: t('address.taxNumber4.placeholder')
  };

  return <Control {...config} />;
}

function DnbNumber({ t, disabled }: CardFieldProps) {
  const field = 'dnbNumber';
  const name = `${category}.address.${field}`;
  const config = {
    name,
    category,
    field,
    label: t('address.dnbNumber.label'),
    validations: [{ type: VALIDATION.numberOfDigits, param: 9 }],
    disabled,
    placeholder: t('address.dnbNumber.placeholder'),
    footer: (
      <Typography
        variant="body2"
        sx={{
          mt: '4px',
          '& a': {
            fontWeight: '400 !important',
            fontSize: '12px !important',
            lineHeight: '16px !important'
          }
        }}
      >
        {t('address.dnbNumber.footer')}
        <a className="sprout-link base" href="https://www.dnb.com/duns-number/lookup.html">
          {' '}
          https://www.dnb.com/duns-number/lookup.html
        </a>
      </Typography>
    )
  };

  return <Control {...config} />;
}

function TaxExempt({ t, dispatch, apiService, store, disabled }: CardFieldProps) {
  const field = `taxExempt`;
  const name = `${category}.address.${field}`;
  const {
    customer: {
      soldTo: {
        address: { taxExempt }
      }
    } = {}
  } = store;
  const attachments = store.attachments.uploads.taxExemptAttachment.files;
  const checkboxConfig = {
    name,
    category,
    field,
    label: t('address.taxExempt.label'),
    type: CONTROL_TYPE.checkbox,
    disabled
  };

  const attachmentsConfig = {
    category,
    field: 'taxExemptAttachment',
    label: t('address.taxExempt.attachments.label'),
    type: CONTROL_TYPE.attachmentList,
    attachments,
    handleRemove: handleRemoveFile(dispatch as CardDispatch, apiService),
    handleUpload: openUploadDialog({
      dispatch: dispatch as CardDispatch,
      apiService,
      // The dialog config's published DialogEvent types don't line up with the
      // upload/cancel callbacks; runtime is correct.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- dialog config uses DialogEvent handlers that don't match the helper's UploadDialog type; runtime is correct
      uploadDialog: taxExemptUploadDialog as any,
      field: 'taxExemptAttachment'
    }),
    handleDownload: (hcId: string | number | undefined, fileName: string) =>
      apiService.downloadFile(hcId, fileName, dispatch),
    shouldShow: getIn(store.customer, name) && !disabled,
    store,
    dispatch: dispatch as CardDispatch
  };

  return (
    <Fragment>
      <Control {...checkboxConfig} />
      {taxExempt && (
        <Typography
          variant="body2"
          sx={{
            mt: '4px',
            '& a': {
              color: '#00843d !important',
              textDecoration: 'underline !important',
              fontWeight: '400 !important',
              fontSize: '12px !important',
              lineHeight: '16px !important',
              cursor: 'pointer',
              transition: 'color 0.2s',
              outline: 'none',
              '&:hover, &:focus': {
                color: 'var(--sprout-palette-green-900) !important',
                outline: 'none'
              }
            }
          }}
        >
          {ReactHtmlParser(t('address.taxExempt.TaxExemptCheckbox'))}
        </Typography>
      )}
      <AttachmentUpload {...attachmentsConfig} />
    </Fragment>
  );
}

function Suframa({ t, disabled, store }: CardFieldProps) {
  let isSuframaRequired = false;
  if (store) {
    const {
      customer: {
        soldTo: {
          address: { zonaFranca }
        }
      }
    } = store;
    isSuframaRequired = zonaFranca === 'Y' || zonaFranca === 'S';
  }
  const field = 'suframa';
  const name = `${category}.address.${field}`;
  const config = {
    name,
    category,
    field,
    label: t('address.suframa.label'),
    validations: [{ type: VALIDATION.number }, { type: VALIDATION.maxCharacters, param: 9 }],
    disabled,
    required: isSuframaRequired,
    placeholder: t('address.suframa.placeholder')
  };

  return <Control {...config} />;
}

function Address({ isReview, t, store, disabled }: CardFieldProps) {
  const field = 'address';
  const name = `${category}.${field}`;
  const {
    customer: { region = '', countryCode = '' }
  } = store;
  let cardSpecificFields: AddressFieldConfig[] = [];
  if (region.toLowerCase() === 'latam' && countryCode.toLowerCase() === 'mx') {
    cardSpecificFields = [
      getAddressFieldConfig('naturalPerson', t, disabled, null, category, false, CONTROL_TYPE.checkbox)
    ];
  }
  const config = {
    disabled: isReview,
    category,
    name,
    label: t(`company.${category}.label`),
    type: CONTROL_TYPE.address,
    addressFields: [...cardSpecificFields, ...getAddressConfigByCategory('soldTo', t, store)]
  };

  return <Control {...config} />;
}

function IcmsTaxPayer({ t, disabled, required }: CardFieldProps) {
  const field = 'icmsTaxPayer';
  const name = `${category}.address.${field}`;
  const config = {
    name,
    category,
    field,
    type: 'select',
    label: t('address.icmsTaxPayer.label'),
    options: t('pickList.icmsTaxPayer', { returnObjects: true }),
    disabled,
    required,
    placeholder: t('address.icmsTaxPayer.placeholder')
  };

  return <Control {...config} />;
}

function ZonaFranca({ t, disabled, store }: CardFieldProps) {
  let iszonaFrancaRequired = false;
  if (store) {
    const {
      customer: {
        soldTo: {
          address: { fiscalId, country }
        },
        region = ''
      }
    } = store;
    if (region.toLowerCase() === 'latam' && country === 'BR') {
      iszonaFrancaRequired = fiscalId === '1' || fiscalId === '2';
    }
  }
  const field = 'zonaFranca';
  const name = `${category}.address.${field}`;
  const config = {
    name,
    category,
    field,
    customStyles: { margin: '0' },
    type: 'select',
    label: t('address.zonaFranca.label'),
    required: iszonaFrancaRequired,
    options: t('pickList.zonaFranca', { returnObjects: true }),
    disabled,
    placeholder: t('address.zonaFranca.placeholder')
  };

  return <Control {...config} />;
}

function TaxType({ t, disabled }: CardFieldProps) {
  const field = 'taxType';
  const name = `${category}.address.${field}`;
  const config = {
    name,
    category,
    field,
    type: 'select',
    label: t('address.taxType.label'),
    options: t('pickList.taxType', { returnObjects: true }),
    disabled,
    placeholder: t('address.taxType.placeholder')
  };

  return <Control {...config} />;
}

function FiscalId({ t, disabled }: CardFieldProps) {
  const field = 'fiscalId';
  const name = `${category}.address.${field}`;
  const config = {
    name,
    category,
    field,
    type: 'select',
    label: t('address.personalOrJuridic.label'),
    options: t('pickList.personalOrJuridic', { returnObjects: true }),
    disabled,
    required: true,
    placeholder: t('address.personalOrJuridic.placeholder')
  };

  return <Control {...config} />;
}

function Emails({ disabled, t, required }: CardFieldProps) {
  const field = 'email';
  const name = `${category}.address.${field}`;
  const config = {
    name,
    field: 'email',
    category,
    label: t('address.email.label'),
    type: CONTROL_TYPE.inputWithTags,
    required: required,
    validations: [
      { type: VALIDATION.array, param: [{ type: VALIDATION.email }] },
      { type: VALIDATION.maxCharacters, param: 255 }
    ],
    onAddTag: ({ store, dispatch }: { store: CardStore; dispatch: CardDispatch }) => {
      // If there is an error on the parent ('invoice delivery') but an email has been entered, clear the error on the parent
      if (typeof store.fieldError[name] === 'string') {
        dispatch(clearFieldError(name));
      }
    },
    placeholder: t('address.email.placeholder'),
    disabled
  };
  return <Control {...config} />;
}

function PhoneNumber1({ disabled, t, required }: CardFieldProps) {
  const field = 'telephone1';
  const name = `${category}.address.${field}`;
  const config = {
    name,
    category,
    field,
    required: required,
    label: t(`address.telephone1.label`),
    placeholder: t(`address.telephone1.placeholder`),
    validations: [{ type: VALIDATION.number }, { type: VALIDATION.maxCharacters, param: 16 }],
    disabled
  };

  return <Control {...config} />;
}

function PhoneNumber2({ disabled, t }: CardFieldProps) {
  const field = 'telephone2';
  const name = `${category}.address.${field}`;
  const config = {
    name,
    category,
    field,
    label: t(`address.telephone2.label`),
    placeholder: t(`address.telephone2.placeholder`),
    validations: [{ type: VALIDATION.number }, { type: VALIDATION.maxCharacters, param: 16 }],
    disabled
  };

  return <Control {...config} />;
}

export function XmlEmail({ disabled, t, dynamicCategory }: CardFieldProps) {
  const field = 'emailXml';
  const resolvedCategory = dynamicCategory || 'soldTo';
  const name = `${resolvedCategory}.address.${field}`;
  const config = {
    name,
    category: resolvedCategory,
    field,
    validations: [{ type: VALIDATION.email }],
    label: t(`address.${field}.label`),
    placeholder: t(`address.${field}.placeholder`),
    disabled
  };

  return <Control {...config} />;
}

export function PaymentSlipEmail({ disabled, t, dynamicCategory }: CardFieldProps) {
  const field = 'emailBoleto';
  const resolvedCategory = dynamicCategory || 'soldTo';
  const name = `${resolvedCategory}.address.${field}`;
  const config = {
    name,
    category: resolvedCategory,
    field,
    validations: [{ type: VALIDATION.email }],
    label: t(`address.${field}.label`),
    placeholder: t(`address.${field}.placeholder`),
    disabled
  };

  return <Control {...config} />;
}

function TradeDbaName({ isReview, store: { editMode: { company } = {} } = {}, t, required }: CardFieldProps) {
  const field = 'tradeDbaName';
  const name = `${companycategory}.${field}`;
  const config = {
    name,
    companycategory,
    field,
    required,
    label: t(`company.${companycategory}.${field}.label`),
    validations: [{ type: VALIDATION.maxCharacters, param: 35 }],
    placeholder: t(`company.${companycategory}.${field}.placeholder`),
    disabled: isReview && !company
  };

  return <Control {...config} />;
}

function LegalName({ isReview, store: { editMode: { company } = {} } = {}, t }: CardFieldProps) {
  const field = 'legalName';
  const name = `${companycategory}.${field}`;
  const config = {
    name,
    companycategory,
    field,
    label: t(`company.${companycategory}.${field}.label`),
    validations: [{ type: VALIDATION.maxCharacters, param: 105 }],
    required: true,
    placeholder: t(`company.${companycategory}.${field}.placeholder`),
    disabled: isReview && !company
  };

  return <Control {...config} />;
}

const controlMap: Record<string, CardFieldComponent> = {
  [SOLD_TO_ADDRESS_FIELDS.taxExempt]: TaxExempt,
  [SOLD_TO_ADDRESS_FIELDS.address]: Address,
  [SOLD_TO_ADDRESS_FIELDS.taxNumber1]: TaxNumber1,
  [SOLD_TO_ADDRESS_FIELDS.taxNumber2]: TaxNumber2,
  [SOLD_TO_ADDRESS_FIELDS.taxNumber3]: TaxNumber3,
  [SOLD_TO_ADDRESS_FIELDS.taxNumber4]: TaxNumber4,
  [SOLD_TO_ADDRESS_FIELDS.dnbNumber]: DnbNumber,
  [SOLD_TO_ADDRESS_FIELDS.zonaFranca]: ZonaFranca,
  [SOLD_TO_ADDRESS_FIELDS.suframa]: Suframa,
  [SOLD_TO_ADDRESS_FIELDS.icmsTaxPayer]: IcmsTaxPayer,
  [SOLD_TO_ADDRESS_FIELDS.taxType]: TaxType,
  [SOLD_TO_ADDRESS_FIELDS.fiscalId]: FiscalId,
  [SOLD_TO_ADDRESS_FIELDS.email]: Emails,
  [SOLD_TO_ADDRESS_FIELDS.telephone1]: PhoneNumber1,
  [SOLD_TO_ADDRESS_FIELDS.telephone2]: PhoneNumber2,
  [SOLD_TO_ADDRESS_FIELDS.paymentSlipEmail]: PaymentSlipEmail,
  [SOLD_TO_ADDRESS_FIELDS.xmlEmail]: XmlEmail,
  [SOLD_TO_ADDRESS_FIELDS.legalName]: LegalName,
  [SOLD_TO_ADDRESS_FIELDS.tradeDbaName]: TradeDbaName
};

function SoldToAddressCard(props: SoldToAddressCardProps) {
  const { isReview, store, fields, errorMustContain } = props;
  const {
    customer: { soldTo }
  } = store;
  const { address } = soldTo;
  const { t } = useTranslation();
  const TotalTaxNumberCount = fields.filter(obj => obj.key?.startsWith('taxNumber')).length;
  const backendTaxNumberCount = Object.keys(address).filter(key => key.startsWith('taxNumber')).length;
  const [currentTaxNumber, setCurrentTaxNumber] = useState(backendTaxNumberCount !== 0 ? backendTaxNumberCount : 1);
  const [disableTaxButton, setDisableTaxButton] = useState(backendTaxNumberCount !== 0 ? backendTaxNumberCount : 0);
  useEffect(() => {
    const backendTaxNumberCount = Object.keys(address).filter(key => key.startsWith('taxNumber')).length;
    setDisableTaxButton(backendTaxNumberCount !== 0 ? backendTaxNumberCount : 0);
    setCurrentTaxNumber(backendTaxNumberCount !== 0 ? backendTaxNumberCount : 1);
  }, [address, setCurrentTaxNumber]);
  const handleAddTaxNumber = () => {
    if (disableTaxButton + 1 > currentTaxNumber) {
      setCurrentTaxNumber(prevNum => prevNum + 1);
    }
  };

  const cardProps = {
    cardId: category,
    title: t('company.soldTo.title'),
    subtitle: t('company.soldTo.subtitle'),
    image: '/food/onboarding/assets/Company_Sold_To.svg',
    isReview,
    store,
    errorMustContain
  };

  return (
    <FormCard {...cardProps}>
      {fields.map(({ key, ...fieldProps }: CardField) => {
        const Component = controlMap[key];
        if (key?.startsWith('taxNumber')) {
          const index = parseInt(key.slice(-1)) - 1;
          // Render tax number fields that are within the current range
          if (index < currentTaxNumber) {
            return (
              <Fragment key={key}>
                <Component t={t} disabled={isReview} {...fieldProps} {...props} />
                {!window.location.href.includes('review') &&
                  currentTaxNumber === index + 1 &&
                  currentTaxNumber !== TotalTaxNumberCount && (
                    <AddTaxNumberButton
                      disabled={!(disableTaxButton + 1 > currentTaxNumber)}
                      onClick={() => handleAddTaxNumber()}
                      t={t}
                    />
                  )}
              </Fragment>
            );
          }
        } else {
          return <Component t={t} disabled={isReview} {...fieldProps} {...props} key={key} />;
        }
        return null;
      })}
    </FormCard>
  );
}

export default SoldToAddressCard;
